import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { EditOutlined } from '@ant-design/icons';
import { getHolidays } from 'actions/profileNew';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import ShowMap from 'pages/attendanceNew/Modals/showMap';
import HolidaysCalendar from 'pages/userManagementV3/holidaysClanedar';
import LocationDrawer from 'pages/userManagementV3/localComponent/locationDrawer';

const timerIcon = require("../../../Assets/userManagementV3/timer.svg").default;
const calendarIcon = require("../dashboardV3/calendar.svg").default;
const locationIcon = require("../../../Assets/userManagementV3/locationIcon.svg").default;

// Users V2 profile "Geofence & Holidays" tab (new design). Same data / API
// calls as pages/userManagementV3/userGeofenceTabV3.js — only the layout and
// cards differ — so the V3 profile keeps its own look.


export const sectionTitleStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: fluid(24),
    fontWeight: 500,
    color: "#191919",
};

export const editLinkStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: fluid(6),
    fontFamily: "Roboto, sans-serif",
    fontSize: fluid(16),
    fontWeight: 700,
    color: "#1089FF",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    padding: 0,
};

// Two cards per row (stacks on narrow screens).
const geofenceGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 420px), 1fr))",
    gap: fluid(16),
};

const PlusCircleIcon = () => (
    <svg width="34" height="34" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="22" cy="22" r="22" fill="#F1EDFF" />
        <path d="M22 14V30M14 22H30" stroke="#594099" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Shown when no geofence is allocated — opens the location drawer (add mode).
const AddGeofenceCard = ({ onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div style={geofenceGridStyle}>
            <button
                type="button"
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: fluid(10),
                    width: "100%",
                    minHeight: fluid(108),
                    padding: fluid(20),
                    borderRadius: fluid(24),
                    border: "2px solid #594099",
                    backgroundColor: hovered ? "#D6D1F0" : "#E1DDF5",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    transition: "background-color 0.15s ease",
                }}
            >
                <PlusCircleIcon />
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(18), fontWeight: 700, letterSpacing: "0.3px", color: "#594099" }}>
                    ADD A NEW GEOFENCE
                </span>
            </button>
        </div>
    );
};

const isTemporaryType = (type) => String(type || '').trim().toLowerCase() === 'temporary';

// Temporary allocation order: active today -> upcoming (by start date) ->
// already ended.
const temporaryRank = (item, today) => {
    const start = item.startDate ? moment(item.startDate) : null;
    const end = item.endDate ? moment(item.endDate) : null;
    if (end && end.isBefore(today, 'day')) return 2;
    if (start && start.isAfter(today, 'day')) return 1;
    return 0;
};

// Every location allocated to the user, from `allAllocations` (allocation
// type / dates + its geoFence details), permanent first, then temporaries
// (current, upcoming, ended). Falls back to the older response fields.
const getLocations = (geoFence) => {
    if (Array.isArray(geoFence?.allAllocations) && geoFence.allAllocations.length > 0) {
        const today = moment();
        const items = geoFence.allAllocations
            .filter((a) => a?.geoFence && !a.geoFence.deleted)
            .map((a) => ({
                ...a.geoFence,
                allocationId: a.id,
                type: a.type,
                startDate: a.startDate,
                endDate: a.endDate,
            }));
        const permanent = items.filter((i) => !isTemporaryType(i.type));
        const temporary = items
            .filter((i) => isTemporaryType(i.type))
            .sort((a, b) => {
                const rankDiff = temporaryRank(a, today) - temporaryRank(b, today);
                if (rankDiff !== 0) return rankDiff;
                return moment(a.startDate || 0).valueOf() - moment(b.startDate || 0).valueOf();
            });
        return [...permanent, ...temporary];
    }
    if (Array.isArray(geoFence?.geofences) && geoFence.geofences.length > 0) {
        return geoFence.geofences;
    }
    if (geoFence?.currentLocation && (geoFence.currentLocation.id || geoFence.currentLocation.title)) {
        return [geoFence.currentLocation];
    }
    return [];
};

const GeofenceCards = ({ geoFence }) => {
    const [mapLocation, setMapLocation] = useState(null);
    const locations = getLocations(geoFence);
    let tempIndex = 0;

    return (
        <div style={geofenceGridStyle}>
            {locations.map((item, index) => {
                const isTemporary = item?.type?.trim().toLowerCase() === 'temporary';
                if (isTemporary) tempIndex += 1;
                const tempCount = locations.filter((l) => l?.type?.trim().toLowerCase() === 'temporary').length;
                const cardTitle = isTemporary
                    ? (tempCount > 1 ? `Temporary location ${tempIndex}` : 'Temporary location')
                    : 'Permanent location';
                const locationTitle = (item?.title && item.title.trim()) || '';
                const rawAddress = (item?.address && item.address.trim()) || '';
                const subtitle = locationTitle || rawAddress;

                let timerText = 'All time';
                if (isTemporary) {
                    if (item.startDate && item.endDate) {
                        timerText = `${moment(item.startDate).format('DD/MM/YYYY')} to ${moment(item.endDate).format('DD/MM/YYYY')}`;
                    } else if (item.startDate) {
                        timerText = `From ${moment(item.startDate).format('DD/MM/YYYY')}`;
                    }
                }

                return (
                    <div
                        key={item.allocationId || item.id || item.geoFenceId || index}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: fluid(14),
                            padding: fluid(28),
                            borderRadius: fluid(20),
                            backgroundColor: "#F6F6F6",
                            boxSizing: "border-box",
                            minWidth: 0,
                        }}
                    >
                        {/* locationIcon.svg includes its own white rounded tile. */}
                        <img src={locationIcon} alt="" style={{ width: fluid(74), height: fluid(74), flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: fluid(6) }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(22), fontWeight: 500, color: "#191919" }}>
                                {cardTitle}
                            </span>
                            {subtitle && (
                                <span title={rawAddress || subtitle} style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {subtitle}
                                </span>
                            )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", alignSelf: "stretch", gap: fluid(10), flexShrink: 0 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: fluid(6), fontFamily: "Roboto, sans-serif", fontSize: fluid(18), fontWeight:400, lineHeight: 1, color: "#191919", whiteSpace: "nowrap" }}>
                                {isTemporary
                                    ? <img src={calendarIcon} alt="" style={{ display: "block", width: fluid(20), height: fluid(20) }} />
                                    : <img src={timerIcon} alt="" style={{ display: "block", width: fluid(20), height: fluid(20) }} />}
                                {timerText}
                            </span>
                            <button
                                type="button"
                                onClick={() => setMapLocation(item || geoFence?.currentLocation)}
                                style={{ ...editLinkStyle, color: "#1089FF", fontSize: fluid(18) }}
                            >
                                VIEW ON MAP
                            </button>
                        </div>
                    </div>
                );
            })}

            {mapLocation && (
                <ShowMap
                    open={Boolean(mapLocation)}
                    onCancel={() => setMapLocation(null)}
                    geoFenceLocationOnMap={mapLocation}
                    whichMapToShow="geoFenceMap"
                />
            )}
        </div>
    );
};

const GeofenceHolidaysTab = ({ getHolidays, holidays, specificUserProfile, setPageLoader, joinedDate }) => {
    const [locationDrawerVisible, setLocationDrawerVisible] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);
    const hasCurrentLocation = Boolean(holidays?.currentLocation && (holidays.currentLocation.id || holidays.currentLocation.title));
    // Show the location cards when anything is allocated (allAllocations can
    // include temporary locations even without a current one).
    const hasAnyLocation = hasCurrentLocation || getLocations(holidays).length > 0;

    const params = {
        edit: undefined,
        geoFenceId: holidays?.currentLocation?.id,
        userId: specificUserProfile?.id,
        year: new Date().getFullYear(),
    };

    useEffect(() => {
        if (specificUserProfile?.id) {
            setPageLoader?.(true);
            getHolidays(params, () => setPageLoader?.(false));
        }
    }, [specificUserProfile?.id]);

    return (
        <div style={{ padding: fluid(24), boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: fluid(16) }}>
                <span style={sectionTitleStyle}>Geofences</span>
                {hasCurrentLocation && (
                    <button type="button" style={editLinkStyle} onClick={() => { setIsAddMode(false); setLocationDrawerVisible(true); }}>
                        <EditOutlined /> EDIT
                    </button>
                )}
            </div>

            {hasAnyLocation ? (
                <GeofenceCards geoFence={holidays} />
            ) : (
                <AddGeofenceCard onClick={() => { setIsAddMode(true); setLocationDrawerVisible(true); }} />
            )}

            <div style={{ marginTop: fluid(28) }}>
                <HolidaysCalendar useId={specificUserProfile?.id} variant="v2" joinedDate={joinedDate} />
            </div>

            <LocationDrawer
                setLocationDrawerVisible={setLocationDrawerVisible}
                locationDrawerVisible={locationDrawerVisible}
                geoFence={holidays}
                getHolidays={getHolidays}
                params={params}
                isAddMode={isAddMode}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.session.user,
    holidays: state.profileNew.holidays,
});

const mapDispatchToProps = (dispatch) => ({
    getHolidays: (params, callback) => dispatch(getHolidays(params, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GeofenceHolidaysTab);
